import "../../../components/common/Tables/DataTable.css"
import React, {useMemo, useState} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { Role } from "../../../redux/models/role.model";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { compareRoles, roleLabel } from "../../../utils/roles";

type Order = 'asc' | 'desc';
const headers = [
  {id:'name', label:'Role'},
  {id:'description', label:'Description'},
  {id:'permissions_count', label:'Permissions', align:'center'},
  {id:'users_count', label:'Users', align:'center'},
];

// Sorted on this page only. The Role column follows the hierarchy
// (Developer > Admin > Corporate > Owner > Dealer > Technician = Sales > Marketing > Customer).
const compare = (a: Role, b: Role, column: string) => {
  switch (column) {
    case 'name':
      return compareRoles(a?.name, b?.name);
    case 'permissions_count':
      return (a?.permissions_count ?? 0) - (b?.permissions_count ?? 0) || compareRoles(a?.name, b?.name);
    case 'users_count':
      return (a?.users_count ?? 0) - (b?.users_count ?? 0) || compareRoles(a?.name, b?.name);
    default:
      return (a?.description ?? '').localeCompare(b?.description ?? '') || compareRoles(a?.name, b?.name);
  }
};

const RolesTable: React.FC = () => {
  const navigate = useNavigate();
  const {data:roles, loading:isLoading, meta} = useSelector((state: RootState) => state.roles);
  const [searchParams, setSearchParams] = useSearchParams();

  // Table Sort: hierarchy by default
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('name');

  const rows = useMemo(() => {
    const direction = order === 'asc' ? 1 : -1;
    return [...(roles ?? [])].sort((a: Role, b: Role) => compare(a, b, orderBy) * direction);
  }, [roles, orderBy, order]);

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(header_id);
  };

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  }

  const cellClass = (header: typeof headers[number]) => header.align ? 'dt-center' : undefined;

  const renderCell = (id: string, role: Role) => {
    switch (id) {
      case 'name':
        return (
          <a
            href={`/roles/${role?.id}`}
            className="dt-name"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(`/roles/${role?.id}`);
            }}
          >
            {roleLabel(role?.name) || "-"}
          </a>
        );

      case 'description':
        return role?.description
          ? <span className="dt-body rl-description" title={role.description}>{role.description}</span>
          : <span className="dt-na">—</span>;

      case 'permissions_count':
        return <span className="dt-num">{(role?.permissions_count ?? 0).toLocaleString()}</span>;

      case 'users_count':
        return <span className="dt-num">{(role?.users_count ?? 0).toLocaleString()}</span>;

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading roles"/></div>
        ) : rows.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((role: Role, i: number)=>(
                <tr key={role?.id ?? i}>
                  {headers.map((header)=>(
                    <td key={header.id} className={cellClass(header)}>
                      {renderCell(header.id, role)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Roles Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={roles?.length ?? 0}
        total={total}
        noun={total === 1 ? 'Role' : 'Roles'}
        page={page}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </div>
  );
};

export default RolesTable;
